import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import Header from "../components/Header";
import SmartBasketBanner from "../components/SmartBasketBanner";
import HomeQuickActionsBar from "../components/HomeQuickActionsBar";
import DailyPlanCard from "../components/DailyPlanCard";
import HomeQuickCompareSection from "../components/HomeQuickCompareSection";
import Footer from "../components/Footer";
import { useAppSelector } from "../store/hooks";
import { apiRequest } from "../api/client";

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function calcSavingsFromComparison(product) {
  const comp = Array.isArray(product?.comparison) ? product.comparison : [];
  if (!comp.length) return null;
  const prices = comp
    .map((item) => parsePrice(item?.price ?? item?.unit_price ?? item?.price_per_unit ?? item?.offer_price))
    .filter((p) => typeof p === "number" && Number.isFinite(p));
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const saving = max - min;
  return saving > 0 && Number.isFinite(saving) ? saving : null;
}

function toQueryListParam(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  const primaryParts = raw
    .split(/\r?\n|,|;|•|●|·|–|—|\s-\s|^\-\s+/gm)
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  const hasExplicitDelimiter = /[\r\n,;•●·–—]/.test(raw);
  const parts =
    !hasExplicitDelimiter && primaryParts.length <= 1
      ? raw
          .split(/\s+/g)
          .map((x) => String(x || "").trim())
          .filter(Boolean)
      : primaryParts;
  const unique = Array.from(new Set(parts));
  return unique.join(",");
}

async function extractTextFromFile(file) {
  const name = String(file?.name || "").toLowerCase();
  const mime = String(file?.type || "").toLowerCase();

  if (mime.includes("text/") || name.endsWith(".txt") || name.endsWith(".csv")) {
    return file.text();
  }

  if (mime.includes("pdf") || name.endsWith(".pdf")) {
    const buffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({
      data: buffer,
    });
    const pdf = await loadingTask.promise;
    const pages = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const line = (content.items || [])
        .map((item) => String(item?.str || "").trim())
        .filter(Boolean)
        .join(" ");
      if (line) pages.push(line);
    }
    return pages.join("\n");
  }

  if (name.endsWith(".doc")) {
    const raw = await file.text();
    const cleaned = String(raw || "")
      .replace(/[\x00-\x08\x0E-\x1F]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!cleaned) {
      throw new Error("Could not read this DOC file. Please convert it to .docx and try again.");
    }
    return cleaned;
  }

  if (
    mime.includes("officedocument.wordprocessingml.document") ||
    name.endsWith(".docx")
  ) {
    const buffer = await file.arrayBuffer();
    try {
      const result = await mammoth.extractRawText({ arrayBuffer: buffer });
      const text = String(result?.value || "").trim();
      if (!text) {
        throw new Error("No readable text found in this DOCX file.");
      }
      return text;
    } catch (_err) {
      throw new Error("Could not read this DOCX file. Please re-save it as .docx and try again.");
    }
  }

  throw new Error("Unsupported file type. Please upload TXT, CSV, PDF, or DOCX.");
}

function FemtyzHome({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const authToken = useAppSelector((s) => s.auth.token);
  const isLoggedIn = Boolean(authToken);
  const features = useAppSelector((s) => s.actowiz.features);
  const [bestOverallBasketState, setBestOverallBasketState] = useState({
    loading: false,
    error: "",
    groceryListId: null,
    savingMessage: "",
    bestOverallBasket: "",
    bannerMode: "new_account", // new_account | list_created | added_list | common_products
    showBestOverallBasketFallback: true,
    hero: {
      variant: "empty", // empty | success | limited | no_matches | locked
      savingsAmount: null,
      retailerName: "",
      basketTotal: null,
      matchedCount: null,
      totalItemCount: null,
      retailerCount: null,
      priceMode: "",
      lastUpdated: "",
      supportingCopy: "",
      isFromApi: false,
    },
  });
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [listPickerOptions, setListPickerOptions] = useState([]);
  const [listPickerResolver, setListPickerResolver] = useState(null);
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [pasteFileErrorPopupOpen, setPasteFileErrorPopupOpen] = useState(false);
  const [pasteFileErrorMessage, setPasteFileErrorMessage] = useState("");

  const closeListPicker = useCallback(() => {
    listPickerResolver?.(null);
    setListPickerOpen(false);
    setListPickerOptions([]);
    setListPickerResolver(null);
  }, [listPickerResolver]);

  const chooseList = useCallback((lists) => {
    return new Promise((resolve) => {
      setListPickerOptions(lists);
      setListPickerResolver(() => resolve);
      setListPickerOpen(true);
    });
  }, []);

  useEffect(() => {
    if (!listPickerOpen) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") closeListPicker();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [listPickerOpen, closeListPicker]);

  useEffect(() => {
    if (!isLoggedIn) {
      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId: null,
        savingMessage: "",
        bestOverallBasket: "",
        bannerMode: "new_account",
        showBestOverallBasketFallback: false,
        hero: {
          variant: "empty",
          savingsAmount: null,
          retailerName: "",
          basketTotal: null,
          matchedCount: null,
          totalItemCount: null,
          retailerCount: null,
          priceMode: "",
          lastUpdated: "",
          supportingCopy: "No active grocery list yet.",
          isFromApi: false,
        },
      });
      return;
    }

    let cancelled = false;
    (async () => {
      setBestOverallBasketState((prev) => ({ ...prev, loading: true, error: "" }));
      const res = await apiRequest("api/users/best_overall_basket", { method: "GET" });
      if (cancelled) return;

      if (!res.ok) {
        setBestOverallBasketState((prev) => ({
          ...prev,
          loading: false,
          error: res.data?.message || "Failed to load best overall basket.",
        }));
        return;
      }

      const message = res.data?.message || "";
      const topSavingMessage = res.data?.saving_message || "";
      const savingAmountFlag = String(res.data?.saving_amount ?? "").trim().toLowerCase();
      const isSubscriptionLocked =
        savingAmountFlag === "no_subscription"
        || savingAmountFlag === "subscription_locked"
        || savingAmountFlag === "trial_locked";

      const bannerPayload =
        res.data?.homepage_smart_basket_banner
        || res.data?.smart_basket_banner
        || res.data?.homepage_smart_basket
        || null;

      const normalizeBannerVariant = (raw) => {
        const key = String(raw || "").trim().toLowerCase();
        if (!key) return "";
        if (["success", "smart_basket_success", "ready"].includes(key)) return "success";
        if (["limited", "limited_comparison", "partial", "weak_coverage"].includes(key)) return "limited";
        if (["no_matches", "no_matched_items", "no_matched_products", "zero_matches"].includes(key)) return "no_matches";
        if (["locked", "subscription_locked", "trial_locked", "gated"].includes(key)) return "locked";
        if (["empty", "visitor", "no_list", "no_active_list", "no_active_grocery_list"].includes(key)) return "empty";
        return "";
      };

      const parseCount = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };

      const apiVariant =
        bannerPayload && typeof bannerPayload === "object"
          ? normalizeBannerVariant(
              bannerPayload?.state ?? bannerPayload?.variant ?? bannerPayload?.status
            )
          : "";

      const apiHero =
        apiVariant
          ? {
              variant: apiVariant,
              savingsAmount:
                parsePrice(
                  bannerPayload?.savingsAmount
                  ?? bannerPayload?.savings_amount
                  ?? bannerPayload?.saveUpTo
                  ?? bannerPayload?.save_up_to
                  ?? null
                ),
              retailerName:
                bannerPayload?.retailerName
                ?? bannerPayload?.retailer_name
                ?? bannerPayload?.bestRetailer
                ?? bannerPayload?.best_retailer
                ?? "",
              basketTotal:
                parsePrice(
                  bannerPayload?.basketTotal
                  ?? bannerPayload?.basket_total
                  ?? bannerPayload?.bestBasketTotal
                  ?? bannerPayload?.best_basket_total
                  ?? null
                ),
              matchedCount:
                parseCount(bannerPayload?.matchedCount ?? bannerPayload?.matched_count ?? bannerPayload?.comparableCount ?? bannerPayload?.comparable_count),
              totalItemCount:
                parseCount(bannerPayload?.totalItemCount ?? bannerPayload?.total_item_count ?? bannerPayload?.totalItems ?? bannerPayload?.total_items),
              retailerCount:
                parseCount(bannerPayload?.retailerCount ?? bannerPayload?.retailer_count),
              priceMode: bannerPayload?.priceMode ?? bannerPayload?.price_mode ?? "",
              lastUpdated: bannerPayload?.lastUpdated ?? bannerPayload?.last_updated ?? "",
              supportingCopy: bannerPayload?.supportingCopy ?? bannerPayload?.supporting_copy ?? "",
              isFromApi: true,
            }
          : null;

      const topLevelLockedHero = isSubscriptionLocked
        ? {
            variant: "locked",
            savingsAmount: null,
            retailerName: "",
            basketTotal: null,
            matchedCount: null,
            totalItemCount: null,
            retailerCount: null,
            priceMode: "",
            lastUpdated: "",
            supportingCopy: res.data?.saving_message || "",
            isFromApi: true,
          }
        : null;

      // If backend provides explicit copy at top-level, prefer it for locked state.
      const topLevelSupportingCopy =
        res.data?.saving_message
        || res.data?.supporting_copy
        || "";
      const topLevelPrimaryCopy = res.data?.primary_copy || "";
      const topLevelBodyCopy = res.data?.body_copy || "";

      // Case 4: common products found (backend provides these fields on grocery_lists)
      const groceryLists = Array.isArray(res.data?.grocery_lists) ? res.data.grocery_lists : [];
      const firstList = groceryLists[0] || null;
      const firstSavingMessage = firstList?.saving_message || "";
      const firstBestOverallBasket = firstList?.best_overall_basket || "";
      if (firstSavingMessage || firstBestOverallBasket) {
        const groceryListId = firstList?.grocery_list_id ?? firstList?.id ?? null;
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId,
          savingMessage: firstSavingMessage,
          bestOverallBasket: firstBestOverallBasket,
          bannerMode: "common_products",
          showBestOverallBasketFallback: true,
          hero:
            topLevelLockedHero
            || apiHero
            || {
              variant: "success",
              savingsAmount: parsePrice(res.data?.save_up_to_amount ?? res.data?.savings_amount ?? null),
              retailerName: "",
              basketTotal: null,
              matchedCount: null,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "",
              isFromApi: false,
            },
        });
        return;
      }

      // Case 1/2/3: no common products / or no lists yet (power_basket_list)
      const power = res.data?.power_basket_list || null;
      const groceryListId = power?.grocery_list_id ?? power?.id ?? null;
      const powerSavingMessage = power?.saving_message || "";

      if (topLevelLockedHero) {
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId: null,
          savingMessage: topLevelSupportingCopy,
          bestOverallBasket: "",
          bannerMode: "new_account",
          showBestOverallBasketFallback: false,
          hero: {
            ...topLevelLockedHero,
            // pass through copy via supportingCopy; the banner's locked copy is fixed,
            // but we keep backend support line.
            supportingCopy: topLevelSupportingCopy,
            primaryCopy: topLevelPrimaryCopy,
            bodyCopy: topLevelBodyCopy,
          },
        });
        return;
      }

      // Case 2: list exists but no products added yet (backend sends top-level saving_message)
      if (!power && topSavingMessage) {
        setBestOverallBasketState({
          loading: false,
          error: "",
          groceryListId: res.data?.grocery_list_id ?? res.data?.id ?? null,
          savingMessage: topSavingMessage,
          bestOverallBasket: "",
          bannerMode: "list_created",
          showBestOverallBasketFallback: false,
          hero:
            apiHero
            || {
              variant: "no_matches",
              savingsAmount: null,
              retailerName: "",
              basketTotal: null,
              matchedCount: 0,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "No matched products yet.",
              isFromApi: false,
            },
        });
        return;
      }

      const totalBasketAmount = parsePrice(power?.total_basket_amount ?? null);
      const lowestBasketText = typeof power?.lowest_basket === "string" ? power.lowest_basket.trim() : "";
      const backendBestOverallBasket =
        typeof power?.best_overall_basket === "string" ? power.best_overall_basket.trim() : "";

      const storeAmounts = power?.total_store_amounts && typeof power.total_store_amounts === "object"
        ? power.total_store_amounts
        : null;

      let secondLowestText = "";
      if (storeAmounts) {
        const entries = Object.entries(storeAmounts)
          .map(([store, amount]) => ({
            store: String(store || "").trim(),
            amount: parsePrice(amount),
          }))
          .filter((x) => x.store && typeof x.amount === "number");

        entries.sort((a, b) => a.amount - b.amount);
        if (entries.length >= 2) {
          const second = entries[1];
          secondLowestText = `2nd lowest basket: ${second.store} (£${second.amount.toFixed(2)})`;
        }
      }

      const bestOverallBasketParts = [];
      // Prefer backend-provided text when available.
      if (backendBestOverallBasket) {
        bestOverallBasketParts.push(backendBestOverallBasket);
      } else {
        if (secondLowestText) bestOverallBasketParts.push(secondLowestText);
        else if (lowestBasketText) bestOverallBasketParts.push(lowestBasketText);
        if (typeof totalBasketAmount === "number") {
          bestOverallBasketParts.push(`Total basket: £${totalBasketAmount.toFixed(2)}`);
        }
      }

      const bannerMode = groceryListId
        ? storeAmounts
          ? "added_list"
          : "list_created"
        : "new_account";

      // Case 1: new account (no list) → do not show basket summary line.
      const bestOverallBasketText =
        bannerMode === "new_account" ? "" : bestOverallBasketParts.join(" • ");

      setBestOverallBasketState({
        loading: false,
        error: "",
        groceryListId,
        savingMessage: powerSavingMessage || topSavingMessage || message,
        bestOverallBasket: bestOverallBasketText,
        bannerMode,
        showBestOverallBasketFallback: bannerMode !== "new_account",
        hero:
          apiHero
          || (bannerMode === "new_account"
            ? {
              variant: "empty",
              savingsAmount: null,
              retailerName: "",
              basketTotal: null,
              matchedCount: null,
              totalItemCount: null,
              retailerCount: null,
              priceMode: "",
              lastUpdated: "",
              supportingCopy: "No active grocery list yet.",
              isFromApi: false,
            }
            : storeAmounts
              ? {
                variant: "limited",
                savingsAmount: null,
                retailerName: "",
                basketTotal: null,
                matchedCount: null,
                totalItemCount: null,
                retailerCount: null,
                priceMode: "",
                lastUpdated: "",
                supportingCopy: bestOverallBasketText || "",
                isFromApi: false,
              }
              : {
                variant: "no_matches",
                savingsAmount: null,
                retailerName: "",
                basketTotal: null,
                matchedCount: 0,
                totalItemCount: null,
                retailerCount: null,
                priceMode: "",
                lastUpdated: "",
                supportingCopy: "No matched products yet.",
                isFromApi: false,
              }),
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  const handleAddToList = useCallback(async (product) => {
    const minimalRes = await apiRequest("api/grocery_lists_minimal", { method: "GET" });
    if (!minimalRes.ok) {
      const errors = Array.isArray(minimalRes.data?.errors) ? minimalRes.data.errors : null;
      throw new Error(
        errors?.join("\n") || minimalRes.data?.message || "Failed to load grocery lists."
      );
    }

    const lists = Array.isArray(minimalRes.data)
      ? minimalRes.data
      : Array.isArray(minimalRes.data?.data)
        ? minimalRes.data.data
        : [];

    if (!lists.length) {
      throw new Error("No grocery list found. Please create a list first.");
    }

    let selectedListId = null;
    if (lists.length === 1) {
      selectedListId =
        lists[0]?.id ?? lists[0]?.grocery_list_id ?? lists[0]?.list_id ?? null;
    } else {
      const selected = await chooseList(lists);
      if (!selected) {
        const cancelError = new Error("ADD_TO_LIST_CANCELLED");
        cancelError.code = "ADD_TO_LIST_CANCELLED";
        throw cancelError;
      }
      selectedListId =
        selected?.id ?? selected?.grocery_list_id ?? selected?.list_id ?? null;
    }
    if (!selectedListId) {
      throw new Error("Unable to select grocery list.");
    }

    const productId = product?.product_id ?? product?.pid ?? product?.sku ?? null;
    if (!productId) {
      throw new Error("Invalid product.");
    }

    const addRes = await apiRequest(`api/grocery_lists/${selectedListId}/grocery_products`, {
      method: "POST",
      body: {
        product_id: String(productId),
        product_name: product?.title || product?.product_name || product?.name || product?.sku || "",
        category: product?.category || product?.category_name || undefined,
        subcategory: product?.subcategory || product?.sub_category || product?.subCategory || undefined,
        platform:
          String(product?.platform || "")
            .trim()
            .toLowerCase()
            .replace("morrisons", "morrison")
            .replace("sainsburys", "sainsbury") || undefined,
        image_url: product?.images?.[0] || "",
        price: product?.price ?? null,
        weight: product?.packsize || product?.weight || "",
        quantity: 1,
      },
    });

    if (!addRes.ok) {
      const errors = Array.isArray(addRes.data?.errors) ? addRes.data.errors : null;
      throw new Error(errors?.join("\n") || addRes.data?.message || "Failed to add product to list.");
    }
  }, [chooseList]);

  const offerTabLowestSaving = (() => {
    const latest12 = (Array.isArray(features) ? features : []).slice(0, 12);
    const savings = latest12
      .map((p) => calcSavingsFromComparison(p))
      .filter((n) => typeof n === "number" && Number.isFinite(n));
    return savings.length ? Math.min(...savings) : null;
  })();

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />

      <main className="mx-auto flex w-full max-w-[90rem] flex-col gap-6 px-4 py-6 md:flex-row md:px-6">

        <section className="flex-1 space-y-8">
          <SmartBasketBanner
            variant={bestOverallBasketState.hero?.variant || "empty"}
            savingsAmount={
              bestOverallBasketState.hero?.savingsAmount
              ?? offerTabLowestSaving
              ?? null
            }
            retailerName={bestOverallBasketState.hero?.retailerName || ""}
            basketTotal={bestOverallBasketState.hero?.basketTotal ?? null}
            matchedCount={bestOverallBasketState.hero?.matchedCount ?? null}
            totalItemCount={bestOverallBasketState.hero?.totalItemCount ?? null}
            retailerCount={bestOverallBasketState.hero?.retailerCount ?? null}
            priceMode={bestOverallBasketState.hero?.priceMode || ""}
            lastUpdated={bestOverallBasketState.hero?.lastUpdated || ""}
            supportingCopy={
              bestOverallBasketState.hero?.supportingCopy
              || (bestOverallBasketState.savingMessage || bestOverallBasketState.bestOverallBasket || "")
            }
            onPrimary={() => {
              const variant = bestOverallBasketState.hero?.variant || "empty";
              if (variant === "locked") {
                navigate("/subscription");
                return;
              }
              if (variant === "empty" || variant === "no_matches") {
                navigate("/settings?tab=grocery");
                return;
              }
              const id = bestOverallBasketState.groceryListId;
              if (id) {
                navigate(
                  `/settings?tab=grocery&view=detail&grocery_list_id=${encodeURIComponent(String(id))}`
                );
              } else {
                navigate("/settings?tab=grocery");
              }
            }}
            onSecondary={() => {
              const variant = bestOverallBasketState.hero?.variant || "empty";
              if (variant === "locked") {
                navigate("/subscription");
                return;
              }
              if (variant === "no_matches") {
                const targetId = "home-listing-start";
                const el =
                  typeof document !== "undefined" ? document.getElementById(targetId) : null;
                if (el && typeof el.scrollIntoView === "function") {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                  return;
                }
                navigate("/search");
                return;
              }
              navigate("/features");
            }}
          />


          {/* Hero / dashboard summary card */}
          <div className="rounded-3xl border border-[#E6EAF0] bg-white px-5 py-8 shadow-sm sm:px-5 sm:py-8">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="max-w-xl">
                <p className="text-[16px] font-normal uppercase tracking-wide text-[#6E7B86]">
                  FEMTYZ Dashboard
                </p>
                <h1 className="mt-2 text-[28px] font-semibold leading-tight text-[#24303A]">
                  Find the cheapest groceries faster
                </h1>
                <p className="mt-2 text-[16px] font-normal text-[#6E7B86]">
                  Compare prices across retailers, then add the best deals to your grocery list.
                </p>

                {/* <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => navigate("/top-deals")}
                    className="rounded-full bg-[#7352C7] px-4 py-2 text-[16px] font-extrabold text-white shadow-sm hover:bg-[#6546c3]"
                  >
                    View Top Deals
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/features")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Browse Featured
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/search")}
                    className="rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[16px] font-extrabold text-[#7352C7] shadow-sm hover:bg-[#F5F7FB]"
                  >
                    Search
                  </button>
                </div> */}
              </div>
              <DailyPlanCard />
            </div>
          </div>
          <HomeQuickActionsBar
            onPasteList={() => {
              if (!isLoggedIn) {
                onOpenAuth?.("login");
                return;
              }
              setPasteText("");
              setSelectedFileName("");
              setPasteFileErrorPopupOpen(false);
              setPasteFileErrorMessage("");
              setPasteModalOpen(true);
            }}
          />
          <div id="home-listing-start" className="scroll-mt-24">
            <HomeQuickCompareSection onAddToList={handleAddToList} />
          </div>
          {/* <SupermarketTabs /> */}
        </section>
      </main>

      <Footer />

      {pasteModalOpen && (
        <div className="fixed inset-0 z-[90]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close paste list modal"
            onClick={() => setPasteModalOpen(false)}
          />
          <div className="relative mx-auto flex min-h-screen max-w-6xl items-center justify-center px-4 py-10">
            <div className="relative w-full max-w-2xl rounded-3xl border border-[#E6EAF0] bg-white p-5 shadow-2xl sm:p-6">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-[22px] font-semibold text-[#24303A]">Paste Grocery List</h3>
                <button
                  type="button"
                  onClick={() => setPasteModalOpen(false)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#E6EAF0] text-slate-600"
                  aria-label="Close popup"
                >
                  ✕
                </button>
              </div>

              <p className="mb-4 text-[14px] text-[#6E7B86] sm:text-[16px]">
                Paste your grocery items or upload txt, csv, pdf, doc, or docx. Extracted text appears below for edits before search.
              </p>

              <div className="mb-3 flex flex-wrap items-center gap-2">
                <label className="cursor-pointer rounded-full border border-[#E6EAF0] bg-white px-4 py-2 text-[14px] font-semibold text-[#24303A] hover:bg-[#F5F7FB]">
                  {isParsingFile ? "Reading file..." : "Upload File"}
                  <input
                    type="file"
                    accept=".txt,.csv,.pdf,.doc,.docx,text/plain,text/csv,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    className="hidden"
                    disabled={isParsingFile}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setPasteFileErrorPopupOpen(false);
                      setPasteFileErrorMessage("");
                      setSelectedFileName(file.name);
                      setIsParsingFile(true);
                      try {
                        const extracted = await extractTextFromFile(file);
                        setPasteText(extracted);
                      } catch (err) {
                        setSelectedFileName("");
                        setPasteFileErrorMessage(
                          err?.message ||
                            "Unable to read this file. Please upload txt, csv, pdf, doc, or docx."
                        );
                        setPasteFileErrorPopupOpen(true);
                      } finally {
                        setIsParsingFile(false);
                        if (e.target) e.target.value = "";
                      }
                    }}
                  />
                </label>
                {selectedFileName && (
                  <span className="text-[13px] font-medium text-[#6E7B86]">{selectedFileName}</span>
                )}
              </div>
              <p className="mb-3 text-[12px] text-[#6E7B86]">
                Accepted formats: TXT, CSV, PDF, DOC, DOCX
              </p>

              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"Example:\nMilk\nEggs\nBread\nTomatoes"}
                className="h-64 w-full resize-none rounded-2xl border border-[#E6EAF0] bg-white px-4 py-3 text-[14px] text-[#24303A] outline-none focus:border-[#7352C7] sm:text-[16px]"
              />

              <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  className="rounded-full border border-[#E6EAF0] bg-white px-5 py-2 text-[14px] font-semibold text-[#24303A] hover:bg-[#F5F7FB] sm:text-[16px]"
                  onClick={() => setPasteModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!String(pasteText || "").trim() || isParsingFile}
                  className="rounded-full bg-[#7352C7] px-5 py-2 text-[14px] font-semibold text-white disabled:opacity-60 sm:text-[16px]"
                  onClick={() => {
                    if (!isLoggedIn) {
                      setPasteModalOpen(false);
                      onOpenAuth?.("login");
                      return;
                    }
                    const query = String(pasteText || "").trim();
                    if (!query) return;
                    const queryList = toQueryListParam(query);
                    if (!queryList) return;
                    setPasteModalOpen(false);
                    navigate(`/search?q=${encodeURIComponent(query)}`);
                  }}
                >
                  Search
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pasteFileErrorPopupOpen && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center px-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close upload error popup"
            onClick={() => setPasteFileErrorPopupOpen(false)}
          />
          <div className="relative w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-6 shadow-xl">
            <h3 className="text-[20px] font-semibold text-[#24303A]">Unable to read file</h3>
            <p className="mt-2 text-[14px] text-[#6E7B86] sm:text-[16px]">
              {pasteFileErrorMessage ||
                "Unable to read this file. Please upload TXT, CSV, PDF, or DOCX."}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setPasteFileErrorPopupOpen(false)}
                className="rounded-full bg-[#7352C7] px-5 py-2 text-[14px] font-semibold text-white sm:text-[16px]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {listPickerOpen && (
        <div className="fixed inset-0 z-[80]">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close list selector"
            onClick={closeListPicker}
          />
          <div className="relative mx-auto flex min-h-screen max-w-3xl items-center justify-center px-4 py-8">
            <div className="w-full max-w-md rounded-2xl border border-[#E6EAF0] bg-white p-5 shadow-xl">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-[#24303A]">Choose Grocery List</h3>
                  <p className="mt-1 text-[16px] text-slate-500">Select the list where you want to add this product.</p>
                </div>
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#E6EAF0] bg-white text-slate-600 hover:bg-slate-50"
                  aria-label="Close popup"
                  title="Close"
                >
                  ✕
                </button>
              </div>
              <div className="mt-4 space-y-2">
                {listPickerOptions.map((list, index) => {
                  const listId = list?.id ?? list?.grocery_list_id ?? list?.list_id ?? "";
                  const listTitle = list?.title || `List ${index + 1}`;
                  return (
                    <button
                      key={`${listId}-${index}`}
                      type="button"
                      className="w-full rounded-xl border border-[#E6EAF0] bg-white px-3 py-2 text-left hover:bg-slate-50"
                      onClick={() => {
                        listPickerResolver?.(list);
                        setListPickerOpen(false);
                        setListPickerOptions([]);
                        setListPickerResolver(null);
                      }}
                    >
                      <p className="text-[16px] font-semibold text-slate-800">{listTitle}</p>
                      <p className="text-[11px] text-slate-500">ID: {String(listId || "-")}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={closeListPicker}
                  className="rounded-lg border border-[#E6EAF0] bg-white px-3 py-1.5 text-[16px] font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FemtyzHome;